import {
    fetchGetCreditCardOnboardingRequirements,
    GetCreditCardOnboardingRequirementsResponse
} from '@iwoca/lapi-client/edge';
import { Text } from '@iwoca/orion-ui';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';

import { OnboardingStepItem } from './onboardingStepItem/OnboardingStepItem';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { deserialiseFundingRequirements } from '../../../funding/fundingSteps/deserialiseFundingRequirements';
import { TFundingRequirementsSteps } from '../../../funding/fundingSteps/orderFundingRequirements';
import IdCheckVerificationModal from '../../../modals/idCheckVerificationModal/IdCheckVerificationModal';
import { ONBOARDING_PATHS } from '../../constants';
import { AddCompanyShareholders } from '../addCompanyShareholders/AddCompanyShareholders';

const OnboardingStep = ({ step }: { step: TFundingRequirementsSteps[0] }) => {
    const type = step.type.toUpperCase();
    switch (type) {
        case 'DEBIT_CARD':
            return (
                <OnboardingStepItem
                    title={
                        <FormattedMessage
                            id="_.credit_card.onboarding.link_debit_card_title"
                            defaultMessage="Set up your payments"
                        />
                    }
                    link={ONBOARDING_PATHS.ONBOARDING_PAYMENT_METHOD}
                />
            );
        case 'ONFIDO_VIDEO_VERIFICATION':
        case 'ONFIDO_VERIFICATION':
        case 'IDVERSE_VERIFICATION':
            return (
                <OnboardingStepItem
                    title={
                        <FormattedMessage
                            id="_.credit_card.onboarding.id_check_title"
                            defaultMessage="Complete your ID check"
                        />
                    }
                    modal={<IdCheckVerificationModal />}
                />
            );
        case 'MAJOR_SHAREHOLDER_CHECK':
            return <AddCompanyShareholders step={step} />;
        default:
            return null;
    }
};

export const OnboardingChecks = () => {
    const stateKey = useStateKey();
    const { data } = useQuery({
        queryKey: ['credit-card-onboarding-requirements', stateKey],
        queryFn: async () => {
            const responseData = await fetchGetCreditCardOnboardingRequirements(
                {
                    stateKey
                }
            );
            return responseData as GetCreditCardOnboardingRequirementsResponse;
        },
        suspense: true,
        select: (response: GetCreditCardOnboardingRequirementsResponse) =>
            response.data.onboarding_requirements
    });

    const sortedOnboardingRequirements =
        //@ts-expect-error: Onboarding requirements response type difference
        deserialiseFundingRequirements(data!).sort(
            (a, b) =>
                (a.status === 'REQUIRED' ? -1 : 0) -
                (b.status === 'REQUIRED' ? -1 : 0)
        );

    return (
        <div>
            <Text className="mt-2xl">
                <h1>
                    <FormattedMessage
                        id="_.credit_card.onboarding.checks.title"
                        defaultMessage="Complete checks"
                    />
                </h1>
                <Text className="text-xl">
                    <FormattedMessage
                        id="_.credit_card.onboarding.checks.sub_title"
                        defaultMessage="You can complete the steps below in any order."
                    />
                </Text>
            </Text>
            <div className="mt-2xl border-t-[1px] border-t-secondary-90">
                {sortedOnboardingRequirements.map(
                    (step: TFundingRequirementsSteps[0], index: number) => (
                        <div
                            id={`funding-step-${index}-${step.type}`}
                            key={`funding-step-${index}-${step.type}`}
                        >
                            <OnboardingStep step={step} />
                        </div>
                    )
                )}
            </div>
        </div>
    );
};
